import { azimuthDelta, formatElevation, normalizeAzimuth, type HorizonResult, type VisiblePeak, type WaterKind, type WaterResult } from "@summit/core";

export interface View {
  /** Azimuth at the centre of the screen, degrees. */
  heading: number;
  /** Horizontal field of view, degrees. */
  fov: number;
  /** Elevation angle at the vertical centre, degrees. */
  pitch: number;
}

export interface PlacedPeak {
  peak: VisiblePeak;
  x: number;
  y: number;
}

const SKY_TOP = "#0d2a55";
const SKY_HORIZON = "#a9c7e8";

/** Colour of water by distance, hazing into the same atmospheric perspective as the terrain. */
function waterColor(t: number, kind: WaterKind, alpha = 1): string {
  const h = 202 + 8 * t;
  const s = 64 - 32 * t;
  // Rivers are lifted a little so a thread of water still reads against the valley it sits in.
  const l = Math.min(86, (kind === "river" ? 34 : 25) + 52 * Math.pow(t, 0.8));
  return `hsla(${h.toFixed(0)}, ${s.toFixed(0)}%, ${l.toFixed(0)}%, ${alpha})`;
}

/** Colour of a terrain layer by distance: near layers dark and saturated, far layers pale and blue. */
function layerColor(t: number, alpha = 1): string {
  // t: 0 = nearest, 1 = farthest
  const h = 205 - 60 * (1 - t) ** 2; // greenish near → blue far
  const s = 22 + 18 * (1 - t);
  const l = 18 + 58 * Math.pow(t, 0.8);
  return `hsla(${h.toFixed(0)}, ${s.toFixed(0)}%, ${l.toFixed(0)}%, ${alpha})`;
}

export class PanoramaRenderer {
  private ctx: CanvasRenderingContext2D;
  private horizon: HorizonResult | null = null;
  private water: WaterResult | null = null;
  private peaks: VisiblePeak[] = [];
  private placed: PlacedPeak[] = [];
  private width = 0;
  private height = 0;
  private dpr = 1;
  view: View = { heading: 180, fov: 80, pitch: 0 };
  /** Camera overlay mode: terrain drawn translucent, no sky. */
  transparent = false;
  /** Draw lakes, rivers and the sea. */
  showWater = true;
  selected: VisiblePeak | null = null;
  private dirty = true;
  private raf = 0;

  constructor(private canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext("2d")!;
    this.resize();
    new ResizeObserver(() => this.resize()).observe(canvas);
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    this.dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    this.width = rect.width;
    this.height = rect.height;
    this.canvas.width = Math.round(rect.width * this.dpr);
    this.canvas.height = Math.round(rect.height * this.dpr);
    this.invalidate();
  }

  setHorizon(h: HorizonResult | null): void {
    this.horizon = h;
    this.invalidate();
  }

  setWater(water: WaterResult | null): void {
    this.water = water;
    this.invalidate();
  }

  setPeaks(peaks: VisiblePeak[]): void {
    this.peaks = peaks.filter((p) => p.visible);
    this.invalidate();
  }

  invalidate(): void {
    this.dirty = true;
    if (!this.raf) this.raf = requestAnimationFrame(() => this.frame());
  }

  private frame(): void {
    this.raf = 0;
    if (this.dirty) {
      this.dirty = false;
      this.render();
    }
  }

  get pxPerDeg(): number {
    return this.width / this.view.fov;
  }

  /** Vertical screen position of the horizon reference (0° elevation minus pitch). */
  private get cy(): number {
    return this.height * (this.transparent ? 0.5 : 0.56);
  }

  screenX(azimuth: number): number {
    return this.width / 2 + azimuthDelta(azimuth, this.view.heading) * this.pxPerDeg;
  }

  screenY(angle: number): number {
    return this.cy - (angle - this.view.pitch) * this.pxPerDeg;
  }

  azimuthAtX(x: number): number {
    return normalizeAzimuth(this.view.heading + (x - this.width / 2) / this.pxPerDeg);
  }

  /** Interpolated band angle at an azimuth. */
  private bandAngle(band: number, azimuth: number): number {
    const h = this.horizon!;
    const n = h.azimuthSteps;
    const pos = (normalizeAzimuth(azimuth) / 360) * n;
    const i0 = Math.floor(pos) % n;
    const i1 = (i0 + 1) % n;
    const f = pos - Math.floor(pos);
    const a = h.bandAngles[band * n + i0];
    const b = h.bandAngles[band * n + i1];
    if (a === -Infinity || b === -Infinity) return -Infinity;
    return a + (b - a) * f;
  }

  /** Peak hit test in CSS pixels. */
  peakAt(x: number, y: number, radius = 22): VisiblePeak | null {
    let best: PlacedPeak | null = null;
    let bestD = radius * radius;
    for (const p of this.placed) {
      const dx = p.x - x;
      const dy = p.y - y;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
    return best?.peak ?? null;
  }

  render(): void {
    const { ctx, width: W, height: H } = this;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    if (!this.transparent) {
      const g = ctx.createLinearGradient(0, 0, 0, Math.max(1, this.screenY(this.view.pitch + this.view.fov * 0.02)));
      g.addColorStop(0, SKY_TOP);
      g.addColorStop(1, SKY_HORIZON);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W, H);
    }

    if (this.horizon) this.drawTerrain();
    if (this.horizon && this.water && this.showWater) this.drawWater();
    this.drawCompass();
    this.placed = [];
    if (this.horizon) this.drawPeaks();
  }

  private drawTerrain(): void {
    const { ctx, width: W, height: H } = this;
    const h = this.horizon!;
    const bandCount = h.bands.length - 1;
    const colStep = 1;
    const cols = Math.ceil(W / colStep) + 1;
    const azimuths = new Float64Array(cols);
    for (let c = 0; c < cols; c++) azimuths[c] = this.azimuthAtX(c * colStep);

    for (let b = bandCount - 1; b >= 0; b--) {
      const t = b / Math.max(1, bandCount - 1);
      ctx.beginPath();
      let any = false;
      let first = true;
      for (let c = 0; c < cols; c++) {
        const angle = this.bandAngle(b, azimuths[c]);
        const y = angle === -Infinity ? H + 20 : this.screenY(angle);
        const x = c * colStep;
        if (first) {
          ctx.moveTo(x, y);
          first = false;
        } else ctx.lineTo(x, y);
        if (y < H) any = true;
      }
      if (!any) continue;
      ctx.lineTo(W + 2, H + 20);
      ctx.lineTo(-2, H + 20);
      ctx.closePath();
      if (this.transparent) {
        ctx.fillStyle = layerColor(t, 0.12);
        ctx.fill();
        ctx.strokeStyle = `hsla(${(205 - 60 * (1 - t) ** 2).toFixed(0)}, 60%, ${(70 + 25 * t).toFixed(0)}%, 0.9)`;
        ctx.lineWidth = 1.2;
        ctx.stroke();
      } else {
        ctx.fillStyle = layerColor(t);
        ctx.fill();
        // subtle rim light on the skyline of each layer
        ctx.strokeStyle = `hsla(${(205 - 60 * (1 - t) ** 2).toFixed(0)}, 30%, ${(30 + 60 * t).toFixed(0)}%, 0.55)`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }
  }

  /** Where a distance falls in the terrain's depth ramp, so water hazes at the same rate. */
  private distanceT(d: number): number {
    const bands = this.horizon!.bands;
    const last = bands.length - 2;
    for (let b = 0; b <= last; b++) {
      if (d < bands[b + 1] || b === last) {
        const f = Math.min(1, Math.max(0, (d - bands[b]) / (bands[b + 1] - bands[b])));
        return Math.min(1, (b + f) / Math.max(1, last));
      }
    }
    return 1;
  }

  /** Inverse of `distanceT`: the distance at a point in the depth ramp. */
  private distanceAtT(t: number): number {
    const bands = this.horizon!.bands;
    const last = bands.length - 2;
    const pos = Math.min(last, Math.max(0, t * last));
    const b = Math.min(last, Math.floor(pos));
    return bands[b] + (pos - b) * (bands[b + 1] - bands[b]);
  }

  /**
   * Water is painted after the terrain: every span has already been line-of-sight tested, so
   * nothing that survives can be behind a nearer ridge. Spans in neighbouring azimuth columns
   * are joined into quads, which keeps shorelines smooth instead of stair-stepped, and each
   * quad is cut along shared iso-distance lines so its colour depends on distance alone —
   * otherwise neighbouring columns haze slightly differently and the lake stripes.
   */
  private drawWater(): void {
    const { ctx, height: H } = this;
    const w = this.water!;
    const steps = w.azimuthSteps;
    const stepDeg = 360 / steps;
    const half = this.view.fov / 2 + stepDeg;
    const barTop = H - 34;
    const SHADES = 256;
    const MAX_SLICES = 40;
    const alpha = this.transparent ? 0.5 : 1;
    const paths: (Path2D | undefined)[] = new Array(SHADES * 2);
    const labels = new Map<string, { name: string; x: number; y: number; h: number; minX: number; maxX: number }>();
    const shadePath = (t: number, river: boolean): Path2D => {
      const i = Math.min(SHADES - 1, Math.max(0, Math.round(t * (SHADES - 1)))) * 2 + (river ? 1 : 0);
      return (paths[i] ??= new Path2D());
    };

    for (let i = Math.floor((this.view.heading - half) / stepDeg); i <= Math.ceil((this.view.heading + half) / stepDeg); i++) {
      const m = ((i % steps) + steps) % steps;
      const m2 = (m + 1) % steps;
      const x = this.screenX(i * stepDeg);
      const x2 = this.screenX((i + 1) * stepDeg);
      for (let k = w.spanIndex[m]; k < w.spanIndex[m + 1]; k++) {
        const f = w.spanFeature[k];
        const lo = w.spanLo[k];
        const hi = w.spanHi[k];
        const near = w.spanNear[k];
        const far = w.spanFar[k];
        // Continue this surface into the next column: the best angle overlap wins, regardless of
        // which OSM feature it came from — a big lake is usually several polygons.
        let lo2 = lo;
        let hi2 = hi;
        let near2 = near;
        let far2 = far;
        let bestOverlap = 0;
        for (let j = w.spanIndex[m2]; j < w.spanIndex[m2 + 1]; j++) {
          const overlap = Math.min(hi, w.spanHi[j]) - Math.max(lo, w.spanLo[j]);
          if (overlap <= -0.05 || overlap < bestOverlap) continue;
          bestOverlap = overlap;
          lo2 = w.spanLo[j];
          hi2 = w.spanHi[j];
          near2 = w.spanNear[j];
          far2 = w.spanFar[j];
        }
        const river = (f < 0 ? "sea" : w.features[f].kind) === "river";

        const topA = this.screenY(hi);
        const botA = this.screenY(lo);
        const topB = this.screenY(hi2);
        const botB = this.screenY(lo2);
        if (Math.min(topA, topB) > barTop || Math.max(botA, botB) < -20) continue;

        const tN = this.distanceT(Math.min(near, near2));
        const tF = this.distanceT(Math.max(far, far2));
        const s0 = Math.floor(tN * (SHADES - 1));
        const shadeSpan = Math.ceil(tF * (SHADES - 1)) - s0;
        const height = Math.max(botA - topA, botB - topB);
        // One slice per ~2.5 px of surface, so a lake filling the screen gets a smooth ramp and the
        // same lake seen edge-on when zoomed out costs a single quad.
        const budget = Math.max(1, Math.min(MAX_SLICES, Math.round(height / 2.5)));
        const stride = Math.max(1, Math.ceil(shadeSpan / budget));
        const slices = Math.ceil(shadeSpan / stride);
        if (slices > 1 && height > 3) {
          // Interpolate the surface angle linearly in distance between the span's two edges.
          const spanA = Math.max(1, far - near);
          const spanB = Math.max(1, far2 - near2);
          let tPrev = tN;
          let yA = this.screenY(lo);
          let yB = this.screenY(lo2);
          for (let n = 0; n < slices; n++) {
            const t = n === slices - 1 ? tF : Math.min(tF, (s0 + (n + 1) * stride) / (SHADES - 1));
            const d = this.distanceAtT(t);
            const yA2 = this.screenY(lo + ((hi - lo) * Math.min(1, Math.max(0, (d - near) / spanA))));
            const yB2 = this.screenY(lo2 + ((hi2 - lo2) * Math.min(1, Math.max(0, (d - near2) / spanB))));
            const path = shadePath((tPrev + t) / 2, river);
            path.moveTo(x, yA2);
            path.lineTo(x2, yB2);
            path.lineTo(x2, yB);
            path.lineTo(x, yA);
            path.closePath();
            tPrev = t;
            yA = yA2;
            yB = yB2;
          }
        } else {
          // Too thin to slice: one quad, with a floor on its height. A river seen at a shallow
          // angle is genuinely sub-pixel; draw it as a thread anyway.
          const minPx = river ? 1.8 : 1.1;
          const thicken = (top: number, bottom: number): [number, number] => {
            if (bottom - top >= minPx) return [top, bottom];
            const c = (top + bottom) / 2;
            return [c - minPx / 2, c + minPx / 2];
          };
          const [tA, bA] = thicken(topA, botA);
          const [tB, bB] = thicken(topB, botB);
          const path = shadePath((tN + tF) / 2, river);
          path.moveTo(x, tA);
          path.lineTo(x2, tB);
          path.lineTo(x2, bB);
          path.lineTo(x, bA);
          path.closePath();
        }

        const name = f >= 0 ? w.features[f].name : undefined;
        if (name) {
          const lab = labels.get(name) ?? { name, x, y: 0, h: -1, minX: Infinity, maxX: -Infinity };
          lab.minX = Math.min(lab.minX, x);
          lab.maxX = Math.max(lab.maxX, x2);
          if (botA - topA > lab.h) {
            lab.h = botA - topA;
            lab.x = x;
            lab.y = (topA + botA) / 2;
          }
          labels.set(name, lab);
        }
      }
    }

    // Farthest shade first, so nearer water paints over the haze behind it.
    for (let i = SHADES * 2 - 1; i >= 0; i--) {
      const path = paths[i];
      if (!path) continue;
      ctx.fillStyle = waterColor((i >> 1) / (SHADES - 1), i % 2 ? "river" : "lake", alpha);
      ctx.fill(path);
    }
    this.drawWaterLabels([...labels.values()], barTop);
  }

  private drawWaterLabels(labels: { name: string; x: number; y: number; h: number; minX: number; maxX: number }[], barTop: number): void {
    const { ctx, width: W } = this;
    ctx.save();
    ctx.font = "italic 500 12px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.shadowColor = "rgba(0, 12, 28, 0.9)";
    ctx.shadowBlur = 3;
    const placed: { x0: number; x1: number; y0: number; y1: number }[] = [];
    let drawn = 0;
    for (const l of labels.sort((a, b) => (b.maxX - b.minX) * (b.h + 3) - (a.maxX - a.minX) * (a.h + 3))) {
      if (drawn >= 10) break;
      const width = ctx.measureText(l.name).width;
      // Only label water wide enough to be recognisable, and never wider than the water itself.
      if (l.maxX - l.minX < 48 || width > l.maxX - l.minX + 30) continue;
      const x = Math.max(width / 2 + 4, Math.min(W - width / 2 - 4, l.x));
      // Thin water (a river, a distant shore) gets the name just below it instead of across it.
      const y = l.h >= 15 ? l.y : l.y + l.h / 2 + 9;
      if (y > barTop - 6 || y < 8) continue;
      const box = { x0: x - width / 2 - 3, x1: x + width / 2 + 3, y0: y - 8, y1: y + 8 };
      if (placed.some((q) => box.x0 < q.x1 && box.x1 > q.x0 && box.y0 < q.y1 && box.y1 > q.y0)) continue;
      placed.push(box);
      ctx.fillStyle = "rgba(226, 241, 255, 0.95)";
      ctx.fillText(l.name, x, y);
      drawn++;
    }
    ctx.restore();
  }

  private drawCompass(): void {
    const { ctx, width: W, height: H } = this;
    const barH = 34;
    const y0 = H - barH;
    ctx.fillStyle = "rgba(8, 14, 26, 0.55)";
    ctx.fillRect(0, y0, W, barH);
    ctx.strokeStyle = "rgba(255,255,255,0.7)";
    ctx.fillStyle = "rgba(255,255,255,0.92)";
    ctx.lineWidth = 1;
    ctx.font = "600 12px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    const start = Math.floor((this.view.heading - this.view.fov / 2) / 5) * 5;
    const end = Math.ceil((this.view.heading + this.view.fov / 2) / 5) * 5;
    const labels: Record<number, string> = { 0: "N", 45: "NE", 90: "E", 135: "SE", 180: "S", 225: "SW", 270: "W", 315: "NW" };
    for (let a = start; a <= end; a += 5) {
      const x = this.screenX(a);
      const az = normalizeAzimuth(a);
      const major = az % 45 === 0;
      const mid = az % 15 === 0;
      ctx.beginPath();
      ctx.moveTo(x, y0);
      ctx.lineTo(x, y0 + (major ? 12 : mid ? 8 : 4));
      ctx.stroke();
      if (major) {
        ctx.fillStyle = az === 0 ? "#ffd166" : "rgba(255,255,255,0.92)";
        ctx.fillText(labels[az], x, y0 + 27);
      } else if (mid && this.pxPerDeg * 15 > 46) {
        ctx.fillStyle = "rgba(255,255,255,0.6)";
        ctx.fillText(`${az}°`, x, y0 + 27);
      }
    }
    // centre marker
    ctx.fillStyle = "#ffd166";
    ctx.beginPath();
    ctx.moveTo(W / 2 - 6, y0 - 8);
    ctx.lineTo(W / 2 + 6, y0 - 8);
    ctx.lineTo(W / 2, y0 - 1);
    ctx.closePath();
    ctx.fill();
  }

  /**
   * How many names to draw.
   *
   * Enough to be useful, never so many that they collide. The budget scales with the width there
   * actually is and with the zoom — a narrow field of view spreads the same peaks further apart,
   * so each can afford more room — and the floor guarantees that a sparse view still gets names
   * rather than a screen of anonymous dots.
   */
  private labelBudget(): { max: number; min: number } {
    const columns = this.width / 46;
    const zoom = Math.min(1.6, Math.max(0.55, 60 / this.view.fov));
    const max = Math.round(Math.max(6, Math.min(30, columns * zoom)));
    return { max, min: Math.min(max, 5) };
  }

  private drawPeaks(): void {
    const { ctx, width: W, height: H } = this;
    const half = this.view.fov / 2 + 2;
    const barTop = H - 34;
    const nameFont = "500 13px system-ui, sans-serif";
    const eleFont = "400 11px system-ui, sans-serif";
    /** Names are drawn rotated, so each label occupies a narrow column this wide. */
    const COLUMN = 17;

    interface Candidate {
      peak: VisiblePeak;
      x: number;
      y: number;
      label?: { lx: number; bottom: number; ele: string };
    }
    const candidates: Candidate[] = [];
    for (const peak of this.peaks) {
      if (Math.abs(azimuthDelta(peak.azimuth, this.view.heading)) >= half) continue;
      const x = this.screenX(peak.azimuth);
      const y = this.screenY(peak.angle);
      if (y > barTop || y < -40) continue;
      candidates.push({ peak, x, y });
      this.placed.push({ peak, x, y });
    }
    // The best spots go to the most important peaks, and a selected peak outranks everything.
    const selectedId = this.selected?.id;
    candidates.sort((a, b) => {
      if (a.peak.id === selectedId) return -1;
      if (b.peak.id === selectedId) return 1;
      return b.peak.score - a.peak.score;
    });

    const occupied: { x: number; top: number; bottom: number }[] = [];
    const free = (lx: number, top: number, bottom: number): boolean =>
      !occupied.some((q) => Math.abs(q.x - lx) < COLUMN && bottom > q.top - 4 && top < q.bottom + 4);

    const measure = (name: string, ele: string): number => {
      ctx.font = nameFont;
      let len = ctx.measureText(name).width + 8;
      if (ele) {
        ctx.font = eleFont;
        len += ctx.measureText(ele).width + 6;
      }
      return len;
    };

    /**
     * Find a clear stretch of column for one label: start just above the summit and slide up past
     * whatever is already there. A placement that still collides is refused rather than drawn over
     * its neighbour — stacking is exactly what turns a crowded ridge into an unreadable pile.
     */
    const place = (c: Candidate, len: number, maxLeader: number, offsets: number[]): { lx: number; bottom: number } | null => {
      let best: { lx: number; bottom: number } | null = null;
      for (const dx of offsets) {
        const lx = c.x + dx;
        if (lx < 8 || lx > W - 8) continue;
        let bottom = c.y - 10 - Math.abs(dx) * 0.6;
        for (let iter = 0; iter < 32; iter++) {
          let next = bottom;
          for (const q of occupied) {
            if (Math.abs(q.x - lx) < COLUMN && bottom > q.top - 4 && bottom - len < q.bottom + 4) next = Math.min(next, q.top - 6);
          }
          if (next === bottom) break;
          bottom = next;
        }
        if (bottom - len < 4) continue; // ran off the top of the screen
        if (c.y - bottom > maxLeader) continue; // leader too long to trace back to its summit
        if (!free(lx, bottom - len, bottom)) continue; // this column is genuinely full
        if (!best || bottom > best.bottom + 12) best = { lx, bottom };
        if (best && dx === 0) break; // a peak's own column always wins
      }
      return best;
    };

    const commit = (c: Candidate, spot: { lx: number; bottom: number }, len: number, ele: string): void => {
      c.label = { lx: spot.lx, bottom: spot.bottom, ele };
      occupied.push({ x: spot.lx, top: spot.bottom - len, bottom: spot.bottom });
    };

    const { max: maxLabels, min: minLabels } = this.labelBudget();
    const NEAR_COLUMNS = [0, COLUMN, -COLUMN, 2 * COLUMN, -2 * COLUMN];
    const WIDE_COLUMNS = [...NEAR_COLUMNS, 3 * COLUMN, -3 * COLUMN, 4 * COLUMN, -4 * COLUMN];
    const shortLeader = Math.max(60, Math.min(150, H * 0.2));
    let labelled = 0;

    // Pass 1: the peaks that matter, each named close to its own summit.
    for (const c of candidates) {
      if (labelled >= maxLabels) break;
      const ele = c.peak.elevationUsed ? formatElevation(c.peak.elevationUsed) : "";
      const len = measure(c.peak.name, ele);
      const spot = place(c, len, shortLeader, NEAR_COLUMNS);
      if (spot) {
        commit(c, spot, len, ele);
        labelled++;
      }
    }

    // Pass 2: if the strict pass found almost nothing — a long name with no room above it, or a
    // cluster all wanting the same column — retry the best remaining peaks with a longer leader,
    // more columns, and if it comes to it the name without its elevation.
    if (labelled < minLabels) {
      const longLeader = Math.max(120, H * 0.42);
      for (const c of candidates) {
        if (labelled >= minLabels) break;
        if (c.label) continue;
        const full = c.peak.elevationUsed ? formatElevation(c.peak.elevationUsed) : "";
        for (const ele of full ? [full, ""] : [""]) {
          const len = measure(c.peak.name, ele);
          const spot = place(c, len, longLeader, WIDE_COLUMNS);
          if (spot) {
            commit(c, spot, len, ele);
            labelled++;
            break;
          }
        }
      }
    }

    // Markers get thinned as well. A ridge can carry a thousand visible summits, and a dot for
    // every one draws a band of speckle along the skyline that reads as noise rather than as
    // peaks. Tapping still finds them all: hit testing uses `placed`, not what was drawn.
    const DOT_GAP = 7;
    const drawnDots: { x: number; y: number }[] = [];
    const wantsDot = (c: Candidate): boolean => {
      if (c.label || c.peak.id === selectedId) return true;
      if (drawnDots.length >= 160) return false;
      return !drawnDots.some((d) => Math.abs(d.x - c.x) < DOT_GAP && Math.abs(d.y - c.y) < DOT_GAP);
    };

    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    for (const c of candidates) {
      const isSel = c.peak.id === selectedId;
      ctx.strokeStyle = isSel ? "#ffd166" : "rgba(255,255,255,0.75)";
      ctx.lineWidth = isSel ? 1.6 : 1;
      if (c.label) {
        ctx.beginPath();
        ctx.moveTo(c.x, c.y - 3);
        ctx.lineTo(c.label.lx, c.label.bottom + 2);
        ctx.stroke();
      }
      if (wantsDot(c)) {
        drawnDots.push({ x: c.x, y: c.y });
        ctx.fillStyle = isSel ? "#ffd166" : c.label ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.35)";
        ctx.beginPath();
        ctx.arc(c.x, c.y, isSel ? 4 : c.label ? 2.5 : 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
      if (!c.label) continue;

      ctx.save();
      ctx.translate(c.label.lx, c.label.bottom);
      ctx.rotate(-Math.PI / 2);
      ctx.font = nameFont;
      ctx.fillStyle = isSel ? "#ffd166" : "#ffffff";
      ctx.shadowColor = "rgba(0,0,0,0.85)";
      ctx.shadowBlur = 3;
      ctx.fillText(c.peak.name, 0, 0);
      if (c.label.ele) {
        const nameW = ctx.measureText(c.peak.name).width;
        ctx.font = eleFont;
        ctx.fillStyle = isSel ? "#ffe6a3" : "rgba(220,230,245,0.9)";
        ctx.fillText(c.label.ele, nameW + 6, 0.5);
      }
      ctx.restore();
    }
  }
}
